import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import {
  getNotices,
  getNoticeDetail,
  createNotice,
  updateNotice,
  deleteNotice,
} from '../services/api';
import type { Notice, NewNotice, NoticeResponse, NoticeListResponse } from '../types/notice';

interface NoticeContextType {
  notices: Notice[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  fetchNotices: (page?: number, limit?: number) => Promise<void>;
  addNotice: (notice: NewNotice) => Promise<boolean>;
  editNotice: (id: number, title: string, content: string, password: string) => Promise<boolean>;
  removeNotice: (id: number, password: string) => Promise<boolean>;
}

const NoticeContext = createContext<NoticeContextType | undefined>(undefined);

export const useNotices = () => {
  const context = useContext(NoticeContext);
  if (!context) {
    throw new Error('useNotices must be used within a NoticeProvider');
  }
  return context;
};

interface NoticeProviderProps {
  children: ReactNode;
}

export const NoticeProvider: React.FC<NoticeProviderProps> = ({ children }) => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });

  const fetchNotices = useCallback(async (page: number = 1, limit: number = 10) => {
    setLoading(true);
    try {
      const response = await getNotices(page, limit);
      if (response.success) {
        setNotices(response.data);
        setPagination(response.pagination);
      }
    } catch (err) {
      setError('공지사항을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  const addNotice = async (notice: NewNotice): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await createNotice(notice);
      if (response.success) {
        await fetchNotices(pagination.page, pagination.limit);
        return true;
      }
      return false;
    } catch (err) {
      setError('공지사항 추가에 실패했습니다.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const editNotice = async (
    id: number,
    title: string,
    content: string,
    password: string
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await updateNotice(id, { title, content, password });
      if (response.success) {
        await fetchNotices(pagination.page, pagination.limit);
        return true;
      }
      return false;
    } catch (err) {
      setError('공지사항 수정에 실패했습니다.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeNotice = async (id: number, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await deleteNotice(id, { password });
      if (response.success) {
        await fetchNotices(pagination.page, pagination.limit);
        return true;
      }
      return false;
    } catch (err) {
      setError('공지사항 삭제에 실패했습니다.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <NoticeContext.Provider
      value={{
        notices,
        loading,
        error,
        pagination,
        fetchNotices,
        addNotice,
        editNotice,
        removeNotice,
      }}
    >
      {children}
    </NoticeContext.Provider>
  );
};
